/**
 * FairnessMetricsPanel Component
 *
 * Summarises fairness KPIs and price disparity from recent runs.
 */

import React, { useEffect, useState } from 'react';
import { FairnessResponse, resultsApi } from '../services/api';
import DataBadge from './DataBadge';
import DataProvenanceRow from './DataProvenanceRow';

interface FairnessMetricsPanelProps {
  runId?: string;
}

const MetricTile: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-3">
    <div className="text-xs text-gray-400 uppercase tracking-wide">{label}</div>
    <div className="text-lg font-semibold text-white">{value}</div>
  </div>
);

const FairnessMetricsPanel: React.FC<FairnessMetricsPanelProps> = ({ runId }) => {
  const [data, setData] = useState<FairnessResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const fetchMetrics = async () => {
      try {
        setLoading(true);
        const response = await resultsApi.getFairness(runId);
        if (!active) return;
        setData(response);
        setError(null);
      } catch (err) {
        if (!active) return;
        setError(err instanceof Error ? err.message : 'Failed to load fairness metrics');
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchMetrics();
    return () => {
      active = false;
    };
  }, [runId]);

  const aggregate = data?.aggregate;
  const price = data?.price_disparity;

  return (
    <div className="bg-gray-800 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-white">Fairness Metrics</h3>
          <DataBadge tier="SCENARIO" />
        </div>
        {data && (
          <span className="text-xs text-gray-400">
            Runs: {data.runs.length}
          </span>
        )}
      </div>

      {data && (
        <DataProvenanceRow
          tier="SCENARIO"
          source="/api/v1/results/fairness"
          asOf={data.generated_at}
          warnings={data.warnings}
          compact
        />
      )}

      {loading && <div className="text-sm text-gray-400">Loading fairness summary...</div>}
      {error && <div className="text-sm text-red-400">{error}</div>}

      {!loading && !error && aggregate && (
        <div className="grid grid-cols-2 gap-3">
          <MetricTile
            label="Unserved Energy"
            value={aggregate.unserved_energy_mwh !== null ? `${aggregate.unserved_energy_mwh.toFixed(1)} MWh` : 'N/A'}
          />
          <MetricTile
            label="Curtailment"
            value={aggregate.curtailment_mwh !== null ? `${aggregate.curtailment_mwh.toFixed(1)} MWh` : 'N/A'}
          />
          <MetricTile
            label="Congestion Hours"
            value={aggregate.congestion_hours !== null ? `${aggregate.congestion_hours.toFixed(1)} h` : 'N/A'}
          />
          <MetricTile
            label="Scarcity Hours"
            value={aggregate.scarcity_hours !== null ? `${aggregate.scarcity_hours.toFixed(1)} h` : 'N/A'}
          />
          <MetricTile
            label="Data Centre Load"
            value={aggregate.data_centre_mwh !== null && aggregate.data_centre_mwh !== undefined
              ? `${aggregate.data_centre_mwh.toFixed(1)} MWh`
              : 'N/A'}
          />
          <MetricTile
            label="DC Backup Used"
            value={aggregate.data_centre_backup_mwh !== null && aggregate.data_centre_backup_mwh !== undefined
              ? `${aggregate.data_centre_backup_mwh.toFixed(1)} MWh`
              : 'N/A'}
          />
        </div>
      )}

      {!loading && !error && price && (
        <div className="border-t border-gray-700 pt-3 text-sm text-gray-300">
          <div className="flex justify-between">
            <span className="text-gray-400">Price Disparity</span>
            <span className="text-white">
              {price.min_price !== null && price.max_price !== null
                ? `${price.min_price.toFixed(1)} → ${price.max_price.toFixed(1)}`
                : 'N/A'}
            </span>
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>Source: {price.source || 'N/A'}</span>
            <span>Ratio: {price.ratio ? price.ratio.toFixed(2) : 'N/A'}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FairnessMetricsPanel;
